/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  InfoIcon component
 */
/** @jsx jsx */

import { chakra, useColorModeValue, useTheme } from '@chakra-ui/react'
import { jsx } from '@emotion/react'
import { useBodyColor } from '@zcode-ui/chakra-themes'
import { Close, Info } from '@zcode-ui/icons'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { EVENT_NAMES, track } from '@zcode/mixpanel'
import { AnalyticsC, trackGAEvent } from '@zcode/nr-utils'
import { voidEvent } from '@zcode/nr/es/shared/utils/utils'
import type { Location } from 'history'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { infoIconBaseStyles, infoIconStyles, infoIconStylesMobile } from './styles'

interface InfoIconProps {
  headerText: string
  location: Location
  setShowInfo: React.Dispatch<React.SetStateAction<boolean>>
  showInfo: boolean
  size?: number
}

export const InfoIcon = ({ location, setShowInfo, showInfo, headerText, size }: InfoIconProps) => {
  const isMobile = useSelector(selectIsMobile)
  const [hovered, setHovered] = useState<boolean>()

  const onMouseOver = useCallback(() => !isMobile && setHovered(true), [isMobile])
  const onMouseOut = useCallback(() => !isMobile && setHovered(false), [isMobile])
  const onClickOpenInfo = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      track({ event: EVENT_NAMES.LIST_ITEM_CARD_INFO })
      const toggleShowInfo = !showInfo
      voidEvent(e)
      setShowInfo(toggleShowInfo)
      // @ts-expect-error -- this is probably an actual bug
      // we're passing a Location object as the "action" being tracked, but `action` should be a string.
      // I *think* this probably shows up as `[object Object]` somewhere in GA, but I can't
      // figure out how to find events in GA
      trackGAEvent(AnalyticsC.LIST, location, 'More Info')
    },
    [location, setShowInfo, showInfo]
  )
  const { colors } = useTheme()
  const { bgColor } = useBodyColor()
  const borderColor = useColorModeValue('gray.150', 'gray.800')
  const iconColor = useColorModeValue(
    hovered ? colors.gray['800'] : colors.gray['600'],
    hovered ? colors.gray['100'] : colors.gray['300']
  )
  const responsiveStyles = isMobile ? infoIconStylesMobile : infoIconStyles
  const styles = {
    ...responsiveStyles,
    ...infoIconBaseStyles,
  }

  return (
    <chakra.button<'button'>
      aria-label={`view more info about ${headerText}`}
      aria-pressed={showInfo}
      bg={bgColor}
      borderColor={borderColor}
      onBlur={onMouseOut}
      onClick={onClickOpenInfo}
      onFocus={onMouseOver}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
      {...styles}
    >
      {isMobile && showInfo ? (
        <Close fill={iconColor} size={10} />
      ) : (
        <Info fill={iconColor} size={size ?? (isMobile ? 18 : 21)} />
      )}
    </chakra.button>
  )
}
